'use client';

import * as React from 'react';

import { cn } from './cn';

/**
 * `Text` — the MUI-free replacement for `Typography`, and the product's TYPE
 * SCALE stated in one place.
 *
 * WHY THE SCALE IS COPIED RATHER THAN INVENTED. The scale below is a faithful
 * transcription of the typography the product actually ships today: MUI's
 * `createTypography` defaults, because `src/providers/mui/theme.ts` overrides
 * only `fontFamily`, `fontWeightSemiBold` and `pageTitle`. Two facts about
 * that transcription were verified in the installed package rather than
 * assumed, and both decide the table:
 *
 * 1. MUI emits `letterSpacing` ONLY when `fontFamily` is its own Roboto/Arial
 *    default (`createTypography.js`: `...(fontFamily === defaultFontFamily ?
 *    { letterSpacing: ... } : {})`). This app sets Inter, so NO variant
 *    carries a letter-spacing override. The new scale therefore declares none
 *    either — a `tracking-*` utility here would be a change, not a migration.
 * 2. `fontSize` defaults to 14 with `htmlFontSize` 16, so `coef = 1` and every
 *    `pxToRem(size)` is exactly `size / 16` rem. That is why the rem column
 *    below is exact rather than approximate: h5 is 1.5rem, h6 is 1.25rem,
 *    body2 is 0.875rem, caption is 0.75rem — the same numbers MUI computed.
 *
 * Those two facts make this table a NO-OP at the medium display size: a
 * migrated `<Typography variant="body2">` renders the identical size, line
 * box and weight it rendered before, which is what lets the reference feature
 * migration be judged on its frames rather than taken on trust.
 *
 * THE ONE DELIBERATE MOVEMENT, AND THE LEGACY SIZE STEP. The most common
 * typography shape in this codebase is a size-only override —
 * `<Typography fontSize={13}>` (25 call sites), `{12}` (9), `{14}` (5),
 * `{12.5}` (4) — which MUI rendered as a FIXED px size: it did not respond to
 * the display-size preference, while every rem-based variant around it did.
 * The `size` prop carries those steps, expressed in root-em (`13` ->
 * 0.8125rem), so at the medium preference the size is EXACTLY what shipped and
 * at small/large it scales with the rest of the scale instead of staying
 * pinned. That is the one place a migrated screen can change size, it is
 * intentional, and it is the direction the design system already scales in
 * everywhere else. `size` overrides only the SIZE: the variant keeps its
 * line-height, weight and element, which is exactly what MUI's `fontSize`
 * override did (it left `lineHeight: 1.5` from body1 in place).
 *
 * WHY THE CLASSES ARE ARBITRARY VALUES (`text-[0.875rem]`) RATHER THAN T-SHIRT
 * NAMES. Tailwind's `text-sm` IS 0.875rem, so the migration could have used
 * it — but then the size would be stated twice (once as the class name, once
 * in the table below) and the two could drift with nothing failing. The scale
 * is stated once, in rem, in `TEXT_TYPE_SCALE`, and the class list is asserted
 * against it in `text.test.tsx` by reading the COMPILED declaration, so a
 * class that disagrees with the table is a red test rather than a silent
 * divergence. `scripts/ui-consistency/contract.ts` reads the same table for
 * its `text:*` type-size expectations.
 *
 * COLOUR DEFAULTS TO INHERIT, BECAUSE MUI'S DID. `Typography` has no default
 * colour (its `color` prop is destructured without one; only `align` defaults),
 * and this app sets no `MuiTypography` defaultProps — so an un-coloured
 * `Typography` inherited whatever its container painted. That matters: MUI
 * `Alert` paints the severity ink on its ROOT and its message slot only sets
 * padding, so `<Alert severity="warning"><Typography>x</Typography></Alert>`
 * rendered warning ink. A `Text` that forced `text-foreground` would silently
 * restyle every such site during the migration, so `color` is opt-in and the
 * default emits no colour class at all. Pass `color="default"` for the
 * explicit `text.primary` MUI's `color="text.primary"` meant.
 *
 * TRACKING IS A PROP, AND `pageTitle` CARRIES IT. MUI only emits
 * `letterSpacing` for its own Roboto/Arial font stack, so none of the twelve
 * transcribed variants has any — but the app's own `theme.typography.pageTitle`
 * DOES set `letterSpacing: '-0.025em'`, which is part of the brand's heading
 * treatment. The variant carries it, and a `tracking` prop exposes the
 * Tailwind steps for anything else.
 *
 * WHAT THIS TABLE IS, AND IS NOT (design review round 1, D5). It is *the
 * scale that ships today*, transcribed from MUI, under MUI's vocabulary. It is
 * NOT the Minerva design system's type scale, and it is named in a vocabulary
 * that collides with it: MUI `h2` is 60px where the brand's `--fs-h2` is 32px,
 * MUI `h6` is 20px where the brand's 20px step is `--fs-h4`, and the brand's
 * headings are Inter Bold at 120% leading with -2% tracking while these
 * headings are 300/400/500 at MUI's leading. Those anchors are recorded here so
 * the eventual "should headings be Bold and tracked?" decision is not silently
 * closed by a table that reads like the design system; `pageTitle` is the one
 * place the app applies the brand treatment today, which is why it is the one
 * entry with a weight, a tracking value and an absolute line box.
 *
 * COLOUR comes from the token bridge, never from a ramp stop:
 * - `default`   `text-foreground`        (`--minerva-palette-text-primary`)
 * - `muted`     `text-muted-foreground`  (`--minerva-palette-text-secondary`, the
 *               ADA-remapped gray[800] — see get-design-tokens)
 * - `disabled`  `text-disabled-text`     (`--minerva-palette-text-disabled`)
 * - `error`     `text-destructive-text`  — DELIBERATE AA fix: MUI's
 *               `color="error"` is `error.main` (#f83b3b), which fails 4.5:1 as
 *               small text; the bridge's scheme-resolved error ink (light
 *               error-600, dark error-400) is what the design system already
 *               uses for error TEXT.
 * - `success` / `warning` / `primary` — the corresponding bridge tokens.
 */

export const TEXT_COLOR_CLASS = {
  /** Explicit `--minerva-palette-text-primary`, which MUI's `color="text.primary"` meant. */
  default: 'text-foreground',
  muted: 'text-muted-foreground',
  disabled: 'text-disabled-text',
  error: 'text-destructive-text',
  success: 'text-success',
  warning: 'text-warning',
  /*
   * `text-primary-text`, NOT `text-primary`, and for the same reason the error
   * tone above is `text-destructive-text`: `primary.main` is a FILL stop, and as
   * TEXT it reads 4.39:1 in dark (below AA) where `primary-text` reads 6.95:1
   * (design review round 1, D2). See the token's note in `src/tailwind.css`.
   */
  primary: 'text-primary-text',
  /** The default: no class, so the container's ink is inherited (see above). */
  inherit: '',
} as const;

export type TextColor = keyof typeof TEXT_COLOR_CLASS;

export type TextWeight = 'normal' | 'medium' | 'semibold' | 'bold';

const TEXT_WEIGHT_CLASS: Record<TextWeight, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

export const TEXT_VARIANTS = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'subtitle1',
  'subtitle2',
  'body1',
  'body2',
  'caption',
  'overline',
  'pageTitle',
] as const;

export type TextVariant = (typeof TEXT_VARIANTS)[number];

/**
 * The product type scale, in root-em — the single source of truth for what
 * each variant measures. `scripts/ui-consistency/contract.ts` reads these
 * numbers for its type-size expectations, and `text.test.tsx` asserts the
 * rendered class declares the same value, so this table cannot go stale
 * against what ships.
 *
 * `lineHeightUnit` exists because one entry does not have a unitless line
 * height: MUI's own `pageTitle` declares `lineHeight: '1.75rem'` — an absolute
 * box, not a ratio — and turning it into 7/6 would render the same 28px at the
 * medium preference while drifting at the others.
 */
export const TEXT_TYPE_SCALE: Record<
  TextVariant,
  {
    fontSizeRem: number;
    lineHeight: number;
    lineHeightUnit: 'ratio' | 'rem';
    fontWeight: number;
  }
> = {
  h1: {
    fontSizeRem: 6,
    lineHeight: 1.167,
    lineHeightUnit: 'ratio',
    fontWeight: 300,
  },
  h2: {
    fontSizeRem: 3.75,
    lineHeight: 1.2,
    lineHeightUnit: 'ratio',
    fontWeight: 300,
  },
  h3: {
    fontSizeRem: 3,
    lineHeight: 1.167,
    lineHeightUnit: 'ratio',
    fontWeight: 400,
  },
  h4: {
    fontSizeRem: 2.125,
    lineHeight: 1.235,
    lineHeightUnit: 'ratio',
    fontWeight: 400,
  },
  h5: {
    fontSizeRem: 1.5,
    lineHeight: 1.334,
    lineHeightUnit: 'ratio',
    fontWeight: 400,
  },
  h6: {
    fontSizeRem: 1.25,
    lineHeight: 1.6,
    lineHeightUnit: 'ratio',
    fontWeight: 500,
  },
  subtitle1: {
    fontSizeRem: 1,
    lineHeight: 1.75,
    lineHeightUnit: 'ratio',
    fontWeight: 400,
  },
  subtitle2: {
    fontSizeRem: 0.875,
    lineHeight: 1.57,
    lineHeightUnit: 'ratio',
    fontWeight: 500,
  },
  body1: {
    fontSizeRem: 1,
    lineHeight: 1.5,
    lineHeightUnit: 'ratio',
    fontWeight: 400,
  },
  body2: {
    fontSizeRem: 0.875,
    lineHeight: 1.43,
    lineHeightUnit: 'ratio',
    fontWeight: 400,
  },
  caption: {
    fontSizeRem: 0.75,
    lineHeight: 1.66,
    lineHeightUnit: 'ratio',
    fontWeight: 400,
  },
  overline: {
    fontSizeRem: 0.75,
    lineHeight: 2.66,
    lineHeightUnit: 'ratio',
    fontWeight: 400,
  },
  // The app's own route-level heading contract (`theme.ts` `typography.
  // pageTitle`): 1.5rem / 1.75rem / 700 / -0.025em / text.primary. Transcribed
  // verbatim, absolute line box and all — see `lineHeightUnit`.
  pageTitle: {
    fontSizeRem: 1.5,
    lineHeight: 1.75,
    lineHeightUnit: 'rem',
    fontWeight: 700,
  },
};

/**
 * The legacy size-only steps: what a bare MUI `fontSize={n}` override rendered,
 * with the variant's own line-height, weight and element left in place. They
 * exist for the migration (prefer a variant in new code) and they are the
 * reason no migrated call site's type size has to round to a variant it did not
 * have. `12.5` is kept rather than snapped: 0.78125rem is exact at medium, and
 * rounding it to 12 was a real 0.5px change at the size most screens are judged
 * at.
 */
export const TEXT_SIZE_SCALE = {
  12: 0.75,
  12.5: 0.78125,
  13: 0.8125,
  14: 0.875,
  // The one rem-string outlier in the codebase: a MUI `fontSize="0.95rem"`,
  // stated here in the same px-at-medium terms as the rest so the union has a
  // single unit. 0.95 * 16 = 15.2.
  15.2: 0.95,
} as const;

export type TextSize = keyof typeof TEXT_SIZE_SCALE;

const TEXT_SIZE_CLASS: Record<TextSize, string> = {
  12: 'text-[0.75rem]',
  12.5: 'text-[0.78125rem]',
  13: 'text-[0.8125rem]',
  14: 'text-[0.875rem]',
  15.2: 'text-[0.95rem]',
};

/**
 * The literal classes the scale above compiles to (Tailwind must see them).
 * The SIZE and the METRICS are separate tables on purpose: `size` replaces the
 * first and keeps the second, and keeping them in one string would mean passing
 * two font-size classes and relying on tailwind-merge's resolution of an
 * ambiguous arbitrary `text-[…]` — which also drops the `leading-*` beside it
 * (measured with tailwind-merge 3.7.0: `text-[1rem] leading-[1.5]
 * text-[0.8125rem]` merges to `text-[0.8125rem]`).
 */
const TEXT_VARIANT_SIZE_CLASS: Record<TextVariant, string> = {
  h1: 'text-[6rem]',
  h2: 'text-[3.75rem]',
  h3: 'text-[3rem]',
  h4: 'text-[2.125rem]',
  h5: 'text-[1.5rem]',
  h6: 'text-[1.25rem]',
  subtitle1: 'text-[1rem]',
  subtitle2: 'text-[0.875rem]',
  body1: 'text-[1rem]',
  body2: 'text-[0.875rem]',
  caption: 'text-[0.75rem]',
  overline: 'text-[0.75rem]',
  pageTitle: 'text-[1.5rem]',
};

const TEXT_VARIANT_METRICS_CLASS: Record<TextVariant, string> = {
  h1: 'leading-[1.167] font-light',
  h2: 'leading-[1.2] font-light',
  h3: 'leading-[1.167] font-normal',
  h4: 'leading-[1.235] font-normal',
  h5: 'leading-[1.334] font-normal',
  h6: 'leading-[1.6] font-medium',
  subtitle1: 'leading-[1.75] font-normal',
  subtitle2: 'leading-[1.57] font-medium',
  body1: 'leading-[1.5] font-normal',
  body2: 'leading-[1.43] font-normal',
  caption: 'leading-[1.66] font-normal',
  overline: 'leading-[2.66] font-normal uppercase',
  // The brand treatment, and the only metrics row with a tracking value or an
  // absolute line box. `text-foreground` is part of the contract (the theme's
  // pageTitle sets `color: var(--minerva-palette-text-primary)`), and a caller's
  // `color` prop still wins because it is composed after this.
  pageTitle: 'leading-[1.75rem] font-bold tracking-[-0.025em] text-foreground',
};

/**
 * The element each variant renders, copied from MUI's `variantMapping` so a
 * migrated tree keeps the DOM it had: a `Typography` with no variant was a
 * `<p>`, and it still is. `h1`-`h6` map to their heading, `subtitle*` to `h6`
 * as MUI did (override with `as` where a heading is wrong for the surface),
 * `caption`/`overline` to `span`, everything else to `p`.
 */
const TEXT_VARIANT_ELEMENT: Record<TextVariant, string> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle1: 'h6',
  subtitle2: 'h6',
  body1: 'p',
  body2: 'p',
  caption: 'span',
  overline: 'span',
  // MUI's `PageTitle` renders `component="h1"`; a route heading stays an h1.
  pageTitle: 'h1',
};

/** The Tailwind tracking steps a caller may ask for. */
export const TEXT_TRACKING_CLASS = {
  tighter: 'tracking-tighter',
  tight: 'tracking-tight',
  normal: 'tracking-normal',
  wide: 'tracking-wide',
  wider: 'tracking-wider',
  widest: 'tracking-widest',
} as const;

export type TextTracking = keyof typeof TEXT_TRACKING_CLASS;

export interface TextProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'color'> {
  /** Overrides the element the variant would render. */
  as?: React.ElementType;
  /** Native label association when `as="label"`; never a generated field ID. */
  htmlFor?: string;
  /**
   * The machine-readable instant when `as="time"`. Declared here, like
   * `htmlFor`, because it is the one native attribute that element needs and
   * `HTMLAttributes` omits it; it is not a general escape hatch.
   */
  dateTime?: string;
  variant?: TextVariant;
  /** A legacy size-only step (see the docblock). Prefer a variant. */
  size?: TextSize;
  /**
   * No default: an un-coloured `Text` inherits its container's ink, exactly as
   * MUI's `Typography` did (see the docblock). `color="default"` is the
   * explicit `text.primary`.
   */
  color?: TextColor;
  /** Overrides the variant's own weight, for the bold-label case. */
  weight?: TextWeight;
  /** Overrides the variant's own letter-spacing (the Tailwind steps). */
  tracking?: TextTracking;
}

export const Text = React.forwardRef<HTMLElement, TextProps>(function Text(
  {
    as,
    className,
    color = 'inherit',
    size,
    tracking,
    variant = 'body1',
    weight,
    ...rest
  },
  ref
) {
  const Component = (as ?? TEXT_VARIANT_ELEMENT[variant]) as React.ElementType;
  return (
    <Component
      ref={ref as React.Ref<never>}
      className={cn(
        'minerva-text',
        // The size, from the variant or from the legacy override — never both.
        size === undefined
          ? TEXT_VARIANT_SIZE_CLASS[variant]
          : TEXT_SIZE_CLASS[size],
        // ...and the variant's own line box, weight and casing, which a size
        // override deliberately does not touch (MUI's `fontSize` did not
        // either: it left the variant's unitless lineHeight in place).
        TEXT_VARIANT_METRICS_CLASS[variant],
        TEXT_COLOR_CLASS[color],
        weight ? TEXT_WEIGHT_CLASS[weight] : '',
        tracking ? TEXT_TRACKING_CLASS[tracking] : '',
        className
      )}
      {...rest}
    />
  );
});
Text.displayName = 'Text';
